import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import SectionHeader from "../SectionHeader";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

// Import translations
import fa from "./locales/UniqueFeatures-fa.json";
import en from "./locales/UniqueFeatures-en.json";
import ar from "./locales/UniqueFeatures-ar.json";
import tr from "./locales/UniqueFeatures-tr.json";

// Icon components
const icons = [
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" key="icon-0">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
        />
    </svg>,
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" key="icon-1">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
        />
    </svg>,
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" key="icon-2">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
    </svg>,
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" key="icon-3">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>,
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" key="icon-4">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
    </svg>,
    <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" key="icon-5">
        <path
            d="M7.5 1.5C10.8137 1.5 13.5 4.18629 13.5 7.5C13.5 10.8137 10.8137 13.5 7.5 13.5C4.18629 13.5 1.5 10.8137 1.5 7.5C1.5 4.18629 4.18629 1.5 7.5 1.5Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M7.5 4.5V7.5L9.75 8.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>,
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" key="icon-6">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>,
];

const FeatureCard: React.FC<{
    feature: {
        title: string;
        icon: React.ReactNode;
        description?: string;
    };
    index: number;
    isActive: boolean;
    setActiveFeature: (index: number | null) => void;
    className?: string;
    isRTL: boolean;
    textAlign: string;
}> = ({ feature, index, isActive, setActiveFeature, className = "", isRTL, textAlign }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(cardRef, { once: true, margin: "-50px" });
    const [isHovered, setIsHovered] = useState(false);

    // پالت رنگی دلبند
    const brandColors = {
        primary: "#0072CE",
        secondary: "#0038A8",
        accent: "#155DFC",
        light: "#BAE6FD",
        pale: "#F0F7FF",
    };

    return (
        <motion.div
            ref={cardRef}
            className={`relative group cursor-pointer`}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 30, scale: 0.95 }}
            transition={{
                duration: 0.6,
                delay: index * 0.15,
                type: "spring",
                stiffness: 100,
            }}
            onHoverStart={() => {
                setActiveFeature(index);
                setIsHovered(true);
            }}
            onHoverEnd={() => {
                setActiveFeature(null);
                setIsHovered(false);
            }}
            onClick={() => setActiveFeature(isActive ? null : index)}
        >
            <motion.div
                className="bg-white/90 backdrop-blur-md p-5 rounded-2xl border border-slate-200/80 h-full relative overflow-hidden flex flex-col shadow-sm"
                whileHover={{
                    scale: 1.02,
                    y: -6,
                    boxShadow: `0 20px 30px -10px rgba(0, 114, 206, 0.15), 0 0 0 1px rgba(0, 114, 206, 0.25)`,
                    borderColor: "rgba(0, 114, 206, 0.3)",
                }}
                whileTap={{ scale: 0.98 }}
                transition={{
                    duration: 0.3,
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                }}
            >
                <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100"
                    style={{
                        background: `linear-gradient(135deg, ${brandColors.pale} 0%, transparent 60%, ${brandColors.light}25 100%)`,
                    }}
                    transition={{ duration: 0.4 }}
                />

                <motion.div
                    className={`absolute top-6 ${isRTL ? "left-8" : "right-8"} w-1 h-1 rounded-full opacity-0 group-hover:opacity-60`}
                    style={{ backgroundColor: brandColors.primary }}
                    animate={
                        isHovered
                            ? {
                                  scale: [1, 1.2, 1],
                                  opacity: [0, 0.6, 0.3],
                              }
                            : {}
                    }
                    transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                />

                <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-start mb-5">
                        <motion.div
                            className={`flex items-center justify-center ${isRTL ? "ml-4" : "mr-4"} p-2.5 rounded-xl relative ${
                                isActive ? "bg-gradient-to-br from-[#0038A8] to-[#0072CE] text-white shadow-md shadow-blue-500/20" : "bg-blue-50 text-[#0072CE]"
                            }`}
                            whileHover={{
                                scale: 1.08,
                            }}
                            transition={{ duration: 0.3 }}
                        >
                            <motion.div
                                animate={
                                    isActive
                                        ? {
                                              scale: [1, 1.15, 1],
                                              rotate: [0, 5, -5, 0],
                                          }
                                        : {}
                                }
                                transition={{ duration: 0.8, repeat: isActive ? Infinity : 0, repeatDelay: 2 }}
                                className="relative z-10"
                            >
                                {feature.icon}
                            </motion.div>
                        </motion.div>

                        <div className="self-center mb-1">
                            <motion.h3 className={`text-lg font-bold text-slate-800 leading-tight ${textAlign}`} animate={isHovered ? { x: isRTL ? -2 : 2 } : { x: 0 }} transition={{ duration: 0.3 }}>
                                {feature.title}
                            </motion.h3>
                        </div>
                    </div>

                    <AnimatePresence>
                        {isActive && (
                            <motion.div
                                initial={{ opacity: 0, height: 0, y: -10 }}
                                animate={{ opacity: 1, height: "auto", y: 0 }}
                                exit={{ opacity: 0, height: 0, y: -10 }}
                                transition={{
                                    duration: 0.3,
                                    type: "spring",
                                    stiffness: 200,
                                }}
                                className="overflow-hidden flex-grow"
                            >
                                <motion.div
                                    className="p-3.5 rounded-xl relative bg-blue-50/60 border border-blue-100/60"
                                    initial={{ scale: 0.95 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.1 }}
                                >
                                    <p className={`text-slate-600 text-sm leading-relaxed ${textAlign}`}>{feature.description}</p>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <motion.div
                    className={`absolute bottom-0 ${isRTL ? "right-0 left-0" : "left-0 right-0"} h-1 rounded-b-2xl`}
                    style={{ background: "linear-gradient(to right, #0038A8, #0072CE, #155DFC)" }}
                    initial={{ width: "0%", opacity: 0 }}
                    animate={{
                        width: isActive ? "100%" : isHovered ? "60%" : "0%",
                        opacity: isActive || isHovered ? 1 : 0,
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                />

                <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none"
                    style={{
                        background: `radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${brandColors.primary}15 0%, transparent 50%)`,
                    }}
                    transition={{ duration: 0.3 }}
                />
            </motion.div>
        </motion.div>
    );
};

const UniqueFeatures: React.FC = () => {
    const { t, i18n } = useTranslation();

    // Register component translations
    useEffect(() => {
        const ns = "UniqueFeatures";
        const resources = { fa, en, ar, tr } as const;

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [activeFeature, setActiveFeature] = useState<number | null>(null);
    const [showAll, setShowAll] = useState<boolean>(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

    // Determine text direction
    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";
    const textAlign = isRTL ? "text-right" : "text-left";

    const features = Array.from({ length: 7 }, (_, index) => {
        const feature = t(`features.${index}`, { ns: "UniqueFeatures", returnObjects: true, defaultValue: null }) as any;
        const moreInfo = t("moreInfo", { ns: "UniqueFeatures" });

        if (feature && feature.title) {
            return {
                title: feature.title,
                description: feature.description || moreInfo,
                icon: icons[index],
            };
        }
        return null;
    }).filter(Boolean) as Array<{ title: string; description: string; icon: React.ReactNode }>;

    return (
        <section ref={sectionRef} dir={dir} id="unique-features" className="relative overflow-hidden">
            <div className="container mx-auto px-4 pb-32 relative z-10">
                <SectionHeader
                    regularText={t("sectionHeader.regularText", { ns: "UniqueFeatures" })}
                    highlightedText={t("sectionHeader.highlightedText", { ns: "UniqueFeatures" })}
                    descriptionHighlight={t("sectionHeader.descriptionHighlight", { ns: "UniqueFeatures" })}
                    descriptionText={t("sectionHeader.descriptionText", { ns: "UniqueFeatures" })}
                />

                <div className="grid grid-cols-12 gap-8 mt-12 justify-center">
                    {features.length > 0 && (
                        <>
                            <div className="col-span-12 md:col-span-5">
                                <FeatureCard
                                    feature={features[0]}
                                    index={0}
                                    isActive={activeFeature === 0}
                                    setActiveFeature={setActiveFeature}
                                    className="h-full"
                                    isRTL={isRTL}
                                    textAlign={textAlign}
                                />
                            </div>
                            {features.length > 1 && (
                                <div className="col-span-12 md:col-span-7">
                                    <FeatureCard
                                        feature={features[1]}
                                        index={1}
                                        isActive={activeFeature === 1}
                                        setActiveFeature={setActiveFeature}
                                        className="h-full"
                                        isRTL={isRTL}
                                        textAlign={textAlign}
                                    />
                                </div>
                            )}

                            {features.length > 2 && (
                                <div className="col-span-12 md:col-span-7">
                                    <FeatureCard
                                        feature={features[2]}
                                        index={2}
                                        isActive={activeFeature === 2}
                                        setActiveFeature={setActiveFeature}
                                        className="h-full"
                                        isRTL={isRTL}
                                        textAlign={textAlign}
                                    />
                                </div>
                            )}

                            {features.length > 3 && (
                                <div className={`col-span-12 md:col-span-5 ${!showAll ? "hidden md:block" : ""}`}>
                                    <FeatureCard
                                        feature={features[3]}
                                        index={3}
                                        isActive={activeFeature === 3}
                                        setActiveFeature={setActiveFeature}
                                        className="h-full"
                                        isRTL={isRTL}
                                        textAlign={textAlign}
                                    />
                                </div>
                            )}

                            {features.length > 4 && (
                                <div className={`col-span-12 md:col-span-4 ${!showAll ? "hidden md:block" : ""}`}>
                                    <FeatureCard
                                        feature={features[4]}
                                        index={4}
                                        isActive={activeFeature === 4}
                                        setActiveFeature={setActiveFeature}
                                        className="h-full"
                                        isRTL={isRTL}
                                        textAlign={textAlign}
                                    />
                                </div>
                            )}
                            {features.length > 5 && (
                                <div className={`col-span-12 md:col-span-4 ${!showAll ? "hidden md:block" : ""}`}>
                                    <FeatureCard
                                        feature={features[5]}
                                        index={5}
                                        isActive={activeFeature === 5}
                                        setActiveFeature={setActiveFeature}
                                        className="h-full"
                                        isRTL={isRTL}
                                        textAlign={textAlign}
                                    />
                                </div>
                            )}
                            {features.length > 6 && (
                                <div className={`col-span-12 md:col-span-4 ${!showAll ? "hidden md:block" : ""}`}>
                                    <FeatureCard
                                        feature={features[6]}
                                        index={6}
                                        isActive={activeFeature === 6}
                                        setActiveFeature={setActiveFeature}
                                        className="h-full"
                                        isRTL={isRTL}
                                        textAlign={textAlign}
                                    />
                                </div>
                            )}

                            {features.length > 3 && (
                                <div className="col-span-12 flex justify-center md:hidden pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowAll((prev) => !prev)}
                                        className="px-6 py-3 rounded-2xl bg-white hover:bg-blue-50/70 text-[#0038A8] border border-blue-200/90 font-bold text-sm transition-all duration-200 flex items-center gap-2.5 shadow-sm hover:shadow-md cursor-pointer"
                                    >
                                        <span>
                                            {showAll
                                                ? t("showLess", { ns: "UniqueFeatures", defaultValue: "مشاهده کمتر" })
                                                : t("showMore", { ns: "UniqueFeatures", defaultValue: "مشاهده بیشتر" })}
                                        </span>
                                        <ChevronDown
                                            className={`w-4 h-4 text-[#0038A8] transform transition-transform duration-300 ${
                                                showAll ? "rotate-180" : "rotate-0"
                                            }`}
                                        />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

export default UniqueFeatures;
